import {
  ACCENT_PRESETS,
  type AccentPresetKey,
  useAccentColor,
  useEditorMode,
  useTheme,
} from "@cheatsheetz/core-ui";
import { useEffect, useRef } from "react";
import { useUserSettings } from "../hooks/useUserSettings";
import { useSession } from "./Session/useSession";

/**
 * Loads the user's display settings from the API on authentication
 * and applies them to the ThemeProvider and AccentColorProvider.
 *
 * Renders nothing — this is a side-effect-only component.
 * Must be placed inside SessionProvider, ThemeProvider, and AccentColorProvider.
 */
export function SettingsSync() {
  const { isAuthenticated } = useSession();
  const { setTheme } = useTheme();
  const { setAccent } = useAccentColor();
  const { setEditorMode } = useEditorMode();
  const { data: settings } = useUserSettings(isAuthenticated);
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!settings || appliedRef.current) return;

    const theme = settings.theme as string | undefined;
    if (theme === "light" || theme === "dark" || theme === "system") {
      setTheme(theme);
    }

    const accent = settings.accent as string | undefined;
    if (accent && accent in ACCENT_PRESETS) {
      setAccent(accent as AccentPresetKey);
    }

    const editorMode = settings.editorMode as string | undefined;
    if (editorMode === "text" || editorMode === "markdown") {
      setEditorMode(editorMode);
    }

    appliedRef.current = true;
  }, [settings, setTheme, setAccent, setEditorMode]);

  // Reset flag on logout so settings are re-applied on next login
  useEffect(() => {
    if (!isAuthenticated) {
      appliedRef.current = false;
    }
  }, [isAuthenticated]);

  return null;
}
